// @ts-nocheck -- RARE-264 mechanical rename; type this file under RARE-265
import { useApolloClient, useLazyQuery, useReactiveVar } from "@apollo/client";
import { useLogto } from "@logto/react";
import { resetFaroUser, setFaroUser } from "@rareconsult/faro";
import * as Sentry from "@sentry/react";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { clearAuthToken, getAuthToken } from "../auth";
import { setAuthFailureHandler } from "../graphql/access-token-bridge";
import {
  AUTH_STATE,
  authError,
  captureReturnTo,
  account as legacyAccount,
  authState as legacyAuthState,
} from "../graphql/policies/auth";
import { CHECK_AUTH, MY_ACCOUNT } from "../graphql/queries/my_account";
import { refreshMonitorToken } from "../graphql/refresh-token";
import { startRecording, stopRecording } from "../recording/session-recorder";
import { publishSignout, subscribeToSignout } from "../tabs";
import { permissionsAllow } from "../tools/permissions";

export const AuthContext = createContext({
  userRolesPermit: () => false,
  userRolesDeny: () => true,
  anyAllow: () => false,
  logOut: async () => void 0,
  logIn: async () => void 0,
  user: { permissions: { allow: [], deny: [] }, id: "" },
  subscription: { settings: {} },
  loggedIn: false,
  authState: AUTH_STATE.WAITING,
  getSetting: (name = "", defaultValue = null) => undefined,
  userSearchBlocks: (segment = "", terms = []) => true,
  refetchAuthData: () => void 0,
});

export const AuthProvider = ({ children }) => {
  const authState = useReactiveVar(legacyAuthState);
  const [loggedIn, setLoggedIn] = useState(false);
  const [subscription, setSubscription] = useState({ settings: {} });
  const [user, setUser] = useState({ permissions: { allow: [], deny: [] } });
  const recordingUserIdRef = useRef(null);
  // Boot runs exactly once per mount. Before MONITOR-135 the bootstrap
  // effect was keyed on the Logto SDK flags, so it re-ran whenever the
  // SDK flipped one mid-session, dropped the live session back into
  // WAITING and silently re-probed. That is illegal now: WAITING is a
  // boot-only state.
  const bootedRef = useRef(false);
  // Logto SDK state. While `isLoading` is true the SDK is still
  // initialising (typically during the OIDC callback exchange) and we
  // should sit in WAITING rather than make a CHECK_AUTH that would
  // pre-empt the Apollo authLink before the access token is ready.
  const {
    isAuthenticated: logtoAuthenticated,
    isLoading: logtoLoading,
    signOut: logtoSignOut,
  } = useLogto();
  // The refresh helper is client-agnostic (see graphql/refresh-token.js);
  // we hand it the same Apollo client the rest of the tree talks to so
  // the mutation rides through authLink / errorLink like any other op.
  const apollo = useApolloClient();

  const [getAccount, { loading, error }] = useLazyQuery(MY_ACCOUNT, {
    fetchPolicy: "network-only",
    onCompleted({ myAccount: { subscription, user } }) {
      legacyAccount(user);
      setSubscription(subscription);
      setUser(user);
    },
  });

  const [checkAuth] = useLazyQuery(CHECK_AUTH, {
    fetchPolicy: "network-only",
    onCompleted: ({ CheckAuth }) => {
      if (CheckAuth) {
        legacyAuthState(AUTH_STATE.LOGGED_IN);
        authError("");
        // getAccount()
      } else {
        captureReturnTo(`${window.location.pathname}${window.location.search}`);
        clearAuthToken();
        legacyAuthState(AUTH_STATE.LOGGED_OUT);
        authError("You have been logged out. Please log in again to continue");
        // Only tear down the Logto SDK session when the SDK actually
        // has one — calling end_session for a legacy LogIn user (no
        // Logto session) issues a no-id_token_hint redirect that
        // Logto handles inconsistently across versions.
        if (logtoAuthenticated) {
          try {
            // /login, not the bare origin: see the auth-failure
            // handler below for why.
            logtoSignOut(`${window.location.origin}/login`);
          } catch (e) {
            // logtoSignOut can throw if the SDK is mid-initialisation;
            // not fatal, the next /login render will retry signIn.
          }
        }
      }
    },
    onError: () => {
      // Deliberately does NOT declare the user logged out.
      //
      // A transport error here is not a verdict. The errorLink owns the
      // 401 decision: it refreshes, retries recoverable failures, and
      // calls the auth-failure handler below only once the session is
      // genuinely over. This handler used to sign the user out
      // independently on *any* transport error, including the
      // REFRESH_FAILED the errorLink synthesises while it was still
      // working the problem, so the two raced and a recoverable blip
      // could be adjudicated as a logout (MONITOR-135).
      //
      // If the failure really is terminal we will hear about it via
      // setAuthFailureHandler a moment later. If it was a blip, the
      // refresh succeeds and nothing should have moved.
      if (legacyAuthState() === AUTH_STATE.WAITING) {
        // The one case still worth resolving here: a boot-time probe
        // that never got an answer would otherwise wedge the spinner
        // forever, because nothing else transitions out of WAITING.
        clearAuthToken();
        legacyAuthState(AUTH_STATE.LOGGED_OUT);
      }
    },
  });

  // Register the auth-failure handler the Apollo errorLink calls when a
  // request comes back as 401 (or one of the gateway's auth-failure
  // extension codes). Forces the same clean Logto sign-out path as a
  // CheckAuth=false result, so a stale cached SDK session can't wedge
  // the spinner on /login.
  useEffect(() => {
    setAuthFailureHandler(({ terminal = true } = {}) => {
      // Capture where the user was BEFORE anything navigates. On the
      // terminal path the next statement takes the browser off this
      // page entirely, and by the time /login renders the location is
      // gone. This is the other half of MONITOR-135: without it the
      // user came back to the home page rather than the page they
      // were on.
      if (typeof window !== "undefined") {
        captureReturnTo(`${window.location.pathname}${window.location.search}`);
      }
      clearAuthToken();
      legacyAuthState(AUTH_STATE.LOGGED_OUT);
      // `authError` is the message channel the sign-in surfaces read.
      // It replaces AUTH_STATE.ERROR, which used to double as both a
      // machine state and a display flag — overloading it is what let a
      // recoverable blip render a terminal-sounding message.
      authError(
        terminal
          ? "You have been signed out by the system. Please sign in again."
          : "We could not reach the server to renew your session. Please sign in again.",
      );
      // Fan the signout out to peer tabs. tabs.js suppresses the post
      // if this handler is itself running inside a received signout,
      // so we can't kick off a cross-tab loop from here.
      publishSignout();
      // Only round-trip through Logto's end_session endpoint when the
      // refresh cookie was actually rejected. A recoverable failure
      // that merely ran out of retries does not justify destroying the
      // IdP session, and the redirect it triggers is expensive.
      if (terminal && logtoAuthenticated) {
        try {
          // Target /login, never the bare origin. `signOut(origin)`
          // lands the user on the home page and the route they were on
          // is lost for good.
          logtoSignOut(`${window.location.origin}/login`);
        } catch (e) {
          // logtoSignOut can throw if the SDK is mid-initialisation;
          // not fatal, the next /login render will retry signIn.
        }
      }
    });
    return () => setAuthFailureHandler(undefined);
  }, [logtoSignOut, logtoAuthenticated]);

  // Cross-tab signout. When another monitor tab broadcasts a signout,
  // tear this tab down the same way an in-tab logout would: drop the
  // token, flip authState to LOGGED_OUT (WithAuth will then route the
  // tree to /login), and hard-navigate as a belt-and-braces so any
  // background work in this tab doesn't keep running against a dead
  // session. The tabs.js subscribe wrapper sets its suppression flag
  // for the duration of the handler, so the auth-failure handler above
  // can't re-broadcast if clearing the token races an in-flight query.
  useEffect(() => {
    return subscribeToSignout(() => {
      if (typeof window !== "undefined") {
        // Capture BEFORE the replace, exactly as the auth-failure
        // handler above does. This path used to omit it, so a peer tab
        // signed out by another tab landed on '/' after signing back
        // in rather than the page it was showing. returnTo lives in
        // per-tab sessionStorage by design, so the tab that broadcast
        // the signout cannot capture on this tab's behalf: each tab
        // has to record its own location. MONITOR-182.
        captureReturnTo(`${window.location.pathname}${window.location.search}`);
      }
      clearAuthToken();
      legacyAuthState(AUTH_STATE.LOGGED_OUT);
      if (typeof window !== "undefined") {
        // replace() rather than assign() so the back button can't
        // return the user to a cached logged-in view.
        window.location.replace("/login");
      }
    });
  }, []);

  // Hydrate the user record when a session becomes live. Coming back
  // from REFRESHING is not a new session — the user record has not
  // changed, only the access token — so skip the refetch there rather
  // than firing a MY_ACCOUNT round trip after every token refresh.
  const previousAuthStateRef = useRef(authState);
  useEffect(() => {
    const previous = previousAuthStateRef.current;
    previousAuthStateRef.current = authState;
    if (authState !== AUTH_STATE.LOGGED_IN) return;
    if (previous === AUTH_STATE.REFRESHING) return;
    getAccount();
  }, [authState]);

  // Reconcile authState across the two sign-in paths and across page
  // reloads.
  //
  // - While the Logto SDK is still resolving (e.g. exchanging the OIDC
  //   code on /callback) stay in WAITING.
  // - While the user is on /callback, Callback owns the transition:
  //   it pulls the Logto access token, calls ExchangeLogtoToken,
  //   writes the monitor JWT to sessionStorage and sets LOGGED_IN.
  //   AuthProvider mustn't pre-empt that — racing CheckAuth against
  //   the in-flight exchange tears down the session the user just
  //   established (symptoms: "AUTH_TOKEN in storage but bumped to
  //   /login", "post-logout sign-in shows loading page then wipes
  //   all session data").
  // - On every other path: if a monitor JWT is already in
  //   sessionStorage, validate it via CheckAuth. The same slot holds
  //   both legacy LogIn JWTs (HS256) and Logto-derived monitor JWTs
  //   (HS256, same secret, with the Logto-bridge claims); the
  //   gateway routes by alg and accepts both, so a single CheckAuth
  //   adjudicates the lot. Without this branch, a page reload after
  //   the legacy LogIn flow drops to LOGGED_OUT (the Logto SDK has
  //   no idea the legacy JWT exists) and the user is bounced to
  //   /login despite a perfectly good token in storage.
  // - No local monitor JWT: this is a fresh tab (sessionStorage is
  //   per-tab by design, see src/auth.js) or a reload after the
  //   token was cleared. The refresh cookie is httpOnly and shared
  //   across tabs, so try a silent RefreshMonitorToken before
  //   deciding the user is signed out. On success we mint a new JWT
  //   into sessionStorage, transition to LOGGED_IN, and let the
  //   getAccount effect below hydrate the user record. On failure
  //   we fall back to the pre-refresh behaviour, quietly: this is a
  //   boot check, not an in-session expiry, and must not raise the
  //   "your session is no longer valid" toast.
  useEffect(() => {
    const onCallback = typeof window !== "undefined" && window.location.pathname === "/callback";
    if (logtoLoading) {
      // Only before boot. Once we have adjudicated the session, a
      // later SDK `isLoading` flip must not drag a live session back
      // into WAITING — that re-probe is what silently signed the user
      // out and then signed them back in (MONITOR-135).
      if (!bootedRef.current) {
        legacyAuthState(AUTH_STATE.WAITING);
      }
      return;
    }
    if (onCallback) {
      return;
    }
    // Boot adjudicates the session exactly once per mount. The effect
    // still depends on the Logto flags so it re-runs when `isLoading`
    // settles, but the body below is a boot, not a poll.
    if (bootedRef.current) {
      return;
    }
    bootedRef.current = true;
    if (getAuthToken()) {
      checkAuth();
      return;
    }
    // Silent rehydrate from the shared refresh cookie. The
    // `cancelled` flag guards against a stale resolution racing a
    // later effect run (StrictMode double-invoke, or
    // logtoAuthenticated flipping mid-refresh).
    legacyAuthState(AUTH_STATE.WAITING);
    let cancelled = false;
    refreshMonitorToken(apollo)
      .then(() => {
        if (cancelled) return;
        // The helper wrote the fresh JWT via setAuthToken; the
        // getAccount effect will fire on the LOGGED_IN transition.
        legacyAuthState(AUTH_STATE.LOGGED_IN);
        authError("");
      })
      .catch(() => {
        if (cancelled) return;
        // Stash the deep link the user actually asked for before the
        // sweep below navigates away, so signing in returns them to it
        // rather than to the home page.
        captureReturnTo(`${window.location.pathname}${window.location.search}`);
        clearAuthToken();
        legacyAuthState(AUTH_STATE.LOGGED_OUT);
        // Sweep an orphan Logto SDK session if one is still hanging
        // around (localStorage-backed, so it can outlive the
        // refresh cookie). No toast on this path.
        if (logtoAuthenticated) {
          try {
            logtoSignOut(`${window.location.origin}/login`);
          } catch (e) {
            // logtoSignOut can throw if the SDK is mid-initialisation;
            // not fatal, the next /login render will retry signIn.
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [logtoAuthenticated, logtoLoading]);

  useEffect(() => {
    if (user && user.id && user.id !== recordingUserIdRef.current) {
      recordingUserIdRef.current = user.id;
      startRecording(user.id, user.email);
      Sentry.setUser({ email: user.email, id: user.id, username: user.name });
      // Faro user association — id only, never email or name.
      setFaroUser(user.id);
    }
    return () => {
      if (recordingUserIdRef.current) {
        stopRecording();
        recordingUserIdRef.current = null;
        Sentry.setUser(undefined);
        resetFaroUser();
      }
    };
  }, [user]);

  const userRolesPermit = useCallback(
    (roles = []) => {
      const r = roles.filter((role) => role.length > 0);
      if (r.length === 0) {
        return true;
      }
      return permissionsAllow(user.permissions, r);
    },
    [user],
  );

  const userRolesDeny = useCallback(
    (roles = []) => {
      const {
        permissions: { deny = [] },
      } = user;
      const denied = deny.filter((p) => roles.includes(p));
      return denied.length > 0;
    },
    [user],
  );

  const anyAllow = useCallback(
    (roles = []) => {
      if (roles.length === 0) {
        return true;
      }
      const {
        permissions: { allow, deny },
      } = user;
      const any = roles
        .filter((role) => !deny.includes(role))
        .filter((role) => allow.includes(role));

      return any.length > 0;
    },
    [user],
  );

  const userSearchBlocks = useCallback(
    (segment = "", terms = []) => {
      const { searches = [] } = subscription;
      const blocksFound = searches.filter((search) => {
        const seg = `${segment}:not`;
        return (
          Object.hasOwn(search, seg) && search[seg].filter((s) => terms.includes(s)).length > 0
        );
      });
      return blocksFound.length > 0;
    },
    [subscription],
  );

  const getSetting = (name, defaultvalue = null) => {
    const { settings } = subscription;
    if (Object.hasOwn(settings, name)) {
      return settings[name];
    } else {
      return defaultvalue;
    }
  };

  const contextValue = {
    logOut: async () => {},
    logIn: async () => {},
    userRolesPermit,
    userRolesDeny,
    anyAllow,
    user,
    subscription,
    loggedIn,
    authState,
    getSetting,
    userSearchBlocks,
    refetchAuthData: getAccount,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
